'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { LogoPhi } from '@/components/brand/logo-phi';

/**
 * Hero visual para el slide "Tarot semanal" del home carousel.
 *
 * Carta de tarot con flip 3D continuo + levitación sutil. Respeta
 * `prefers-reduced-motion` (fallback: carta estática con el reverso Φ).
 *
 * Identidad ritual: la CARTA misma siempre es dorada (objeto físico
 * inmutable). El fondo de la escena, halo y página responden al theme
 * (`--color-bg`, `--color-fg`, `--color-ritual`).
 */
export function TarotHeroAnimation() {
  const reduced = useReducedMotion();

  const flip = reduced
    ? {}
    : {
        rotateY: [0, 180, 180, 0],
        y: [0, -14, -14, 0],
      };

  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-[var(--color-bg)]">
      {/* Halo dorado que envuelve la carta — color ritual con baja opacidad
          para que se vea natural en ambos modes. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 45%, color-mix(in oklch, var(--color-ritual) 22%, transparent) 0%, transparent 55%)',
        }}
      />

      <div
        className="relative h-[380px] w-[240px] md:h-[520px] md:w-[320px]"
        style={{ perspective: '1400px' }}
      >
        <motion.div
          className="relative h-full w-full"
          style={{ transformStyle: 'preserve-3d' }}
          initial={{ rotateY: 0, y: 0 }}
          animate={flip}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.38, 0.62, 1],
          }}
        >
          {/* Reverso — fondo noir ritual con Φ oro.
              Mantiene paleta noir porque la carta es un objeto físico:
              su reverso siempre tiene el patrón dorado sobre lacquer noir,
              independientemente del bg del sitio. */}
          <div
            className="absolute inset-0 flex items-center justify-center rounded-sm border bg-gradient-to-br from-[#1a1816] via-[#121110] to-[#0B0B0A]"
            style={{
              backfaceVisibility: 'hidden',
              borderColor: 'var(--color-ritual)',
              boxShadow: '0 30px 80px color-mix(in oklch, var(--color-ritual) 28%, transparent)',
            }}
          >
            <div
              className="absolute inset-4 rounded-sm border"
              style={{ borderColor: 'color-mix(in oklch, var(--color-ritual) 25%, transparent)' }}
            />
            <div className="flex flex-col items-center gap-5">
              <LogoPhi size={72} tone="gold" />
              <p
                className="text-[10px] font-bold tracking-[0.3em] uppercase"
                style={{
                  color: 'color-mix(in oklch, var(--color-ritual) 85%, transparent)',
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                }}
              >
                Φ · TAROT
              </p>
            </div>
          </div>

          {/* Anverso — gradiente dorado del arcano.
              El texto del arcano es noir (ritual-fg en oro siempre es noir). */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center rounded-sm border bg-gradient-to-br from-[#d9b577] via-[#b48948] to-[#8a6d3c]"
            style={{
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              borderColor: 'var(--color-ritual)',
              boxShadow: '0 30px 80px color-mix(in oklch, var(--color-ritual) 40%, transparent)',
            }}
          >
            <div className="absolute inset-4 rounded-sm border border-[#0B0B0A]/30" />
            <span
              className="text-[10px] font-bold tracking-[0.4em] text-[#0B0B0A] uppercase"
              style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
            >
              IX
            </span>
            <div className="my-4">
              <LogoPhi size={96} tone="noir" />
            </div>
            <span
              className="text-[10px] font-bold tracking-[0.3em] text-[#0B0B0A] uppercase"
              style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
            >
              ARCANO
            </span>
          </div>
        </motion.div>
      </div>

      {/* Gradiente sutil bottom→top para legibilidad del overlay tipográfico
          que vive en HeroCarousel (eyebrow · title · subtitle · CTA). Usa
          --color-bg para fundirse con el fondo en ambos modes. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, color-mix(in oklch, var(--color-bg) 80%, transparent), color-mix(in oklch, var(--color-bg) 10%, transparent), transparent)',
        }}
      />
    </div>
  );
}
